import type { AdminMcpToken, AdminMcpTokenCreated } from "@storyline/shared";
import { useCallback, useEffect, useState } from "react";
import { HttpError } from "@/src/common/services/api-error";
import { createMcpToken, fetchMcpTokens, revokeMcpToken } from "@/src/settings/services/mcp-tokens-http.service";

const GENERIC_ERROR = "Une erreur est survenue. Veuillez réessayer.";

const toMessage = (caught: unknown): string => (caught instanceof HttpError ? caught.message : GENERIC_ERROR);

export const useMcpTokens = () => {
	const [tokens, setTokens] = useState<AdminMcpToken[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isCreating, setIsCreating] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [created, setCreated] = useState<AdminMcpTokenCreated | null>(null);

	const reload = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await fetchMcpTokens();
			setTokens(data.tokens);
			setHasError(false);
		} catch {
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void reload();
	}, [reload]);

	const create = useCallback(async (name: string): Promise<void> => {
		setError(null);
		setIsCreating(true);
		try {
			const result = await createMcpToken(name);
			setCreated(result);
			setTokens((previous) => [result.token, ...previous]);
		} catch (caught) {
			setError(toMessage(caught));
		} finally {
			setIsCreating(false);
		}
	}, []);

	const revoke = useCallback(async (id: string): Promise<void> => {
		setError(null);
		try {
			await revokeMcpToken(id);
			setTokens((previous) => previous.filter((token) => token.id !== id));
			setCreated((previous) => (previous?.token.id === id ? null : previous));
		} catch (caught) {
			setError(toMessage(caught));
		}
	}, []);

	const dismissCreated = useCallback(() => {
		setCreated(null);
	}, []);

	return { tokens, isLoading, isCreating, hasError, error, created, create, revoke, dismissCreated, reload };
};
